import ReactGA from "react-ga4";

const MEASUREMENT_ID = process.env.REACT_APP_GA_MEASUREMENT_ID;

ReactGA.initialize(MEASUREMENT_ID, {
  debug: process.env.REACT_APP_ENV !== "prod"
});

const engagementTrackers = {};

// Cleanup helper (defined first so it can be referenced inside the timer)
const cleanup = (userId) => {
  if (engagementTrackers[userId]) {
    clearTimeout(engagementTrackers[userId].timer);
    engagementTrackers[userId].interactions.forEach((fn) => fn());
    delete engagementTrackers[userId];
  }
};

/**
 * Tracks user engagement duration and interactions via GA4
 * @param {string} userId - The user ID to track
 */
export const trackUserEngagement = (userId) => {
  if (!userId) return;

  // Clear previous tracker if exists
  if (engagementTrackers[userId]) {
    cleanup(userId);
  }

  const startTime = Date.now();

  const getDurationInSeconds = () =>
    Math.round((Date.now() - startTime) / 1000);

  // Time-based engagement fires after 30 seconds
  const timer = setTimeout(() => {
    trackEvent("userEngagement", {
      userID: userId,
      engagement_duration: getDurationInSeconds()
    });
    cleanup(userId);
  }, 30000);

  // Also fire on any user interaction
  const interactionHandler = () => {
    trackEvent("userEngagement", {
      userID: userId,
      engagement_duration: getDurationInSeconds()
    });
  };

  const events = ["click", "scroll", "keydown"];
  const interactionCleanups = events.map((event) => {
    globalThis.addEventListener(event, interactionHandler, { passive: true });
    return () => globalThis.removeEventListener(event, interactionHandler);
  });

  engagementTrackers[userId] = {
    timer,
    interactions: interactionCleanups,
    startTime
  };
};

// Identify users
export const identifyUser = (userId) => {
  ReactGA.set({ user_id: userId });
};

// Track custom events
export const trackEvent = (eventName, properties = {}) => {
  ReactGA.event(eventName, {
    ...properties,
    environment: globalThis.location.hostname
  });
};

// Set user properties
export const setUserProperties = (properties) => {
  ReactGA.set(properties);
};

// Set user properties only once (GA4 does not support `set_once` like Mixpanel)
export const setOnceUserProperties = (properties) => {
  ReactGA.set(properties);
};

// Increment user login count (GA4 does not support incrementing directly)
export const userLoginCountProperties = (properties) => {
  trackEvent("user_login_count", properties);
};

// Reset user session
export const reset = () => {
  ReactGA.set({ user_id: null });
};
